'use client'

import { PropsWithChildren, useTransition } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Flex, Heading } from '@chakra-ui/react'
import { ProductCategory } from '@leid/api-products'

import { CategoriesInfo } from 'app/(pages)/browse/[...categoryName]/helpers'
import { BrowsePageSearchParams } from 'app/(pages)/browse/page'
import Container from 'app/components/shared/Container'
import { getCategoryName, getCategoryURL } from 'app/components/shared/nav/CategoriesMenu/utils/categories'
import Pagination from 'app/components/shared/Pagination'
import { CategoryCard } from 'app/components/shared/ui/cards/CategoryCard'
import { CustomerInfo } from 'app/utils/authentication'
import { SortingInfo } from 'appTypes/mapper'
import { Metadata } from 'appTypes/shared'
import { SearchForProducts } from 'components/form/SearchForProducts'
import CategoriesBreadcrumbs from 'components/partials/browse/CategoriesBreadcrumbs'
import { Loader } from 'components/ui/Loader'
import { SectionHeading } from 'components/ui/Text/SectionHeading'

import { BrowseTopBar } from './BrowseTopBar'
import CategoriesCards from './CategoriesCards'
import SortButton from './SortButton'
import TransitionLink from './TransitionLink'

type Props = PropsWithChildren & {
  isBundles?: boolean
  categories: ProductCategory[]
  metadata: Metadata | undefined
  query: {
    [key: string]: string | string[] | undefined
  }
  sortingInfo: SortingInfo | undefined
  customerInfo: CustomerInfo
  searchParams?: BrowsePageSearchParams
  categoriesInfo?: CategoriesInfo
  breadcrumbs?: {
    url: string
    label: string
  }[]
}

const BrowseNavigationContext = ({
  children,
  isBundles,
  categories,
  metadata,
  query,
  sortingInfo,
  customerInfo,
  searchParams,
  categoriesInfo,
  breadcrumbs,
}: Props): JSX.Element => {
  const { t } = useTranslation()

  const [isPending, startTransition] = useTransition()

  return (
    <>
      <BrowseTopBar
        isBundles={isBundles ?? false}
        parentCategories={categoriesInfo?.parentCategories}
        searchCategory={searchParams?.category}
        allCategories={categories}
        queryParams={query}
        startTransition={startTransition}
      />

      {categoriesInfo === undefined ? (
        <>
          {!isBundles ? (
            <>
              <Box display={{ md: 'none' }} marginTop="20px">
                <Container>
                  <Flex marginBottom="36px" justifyContent="space-between" alignItems="center">
                    <SectionHeading>{t('browseProducts')}</SectionHeading>
                  </Flex>
                  <SearchForProducts redirectTo="browse" />
                </Container>
              </Box>

              <CategoriesCards
                categories={categories}
                location={searchParams?.location}
                startTransition={startTransition}
              />

              <Box>
                <Container>
                  <Flex direction="row" justifyContent="flex-end" marginTop="20px" wordBreak="break-word">
                    <SortButton
                      sortingInfo={sortingInfo}
                      customerInfo={customerInfo}
                      startTransition={startTransition}
                    />
                  </Flex>
                </Container>
              </Box>
            </>
          ) : (
            <>
              <Box marginTop={{ base: '20px', lg: '60px' }} marginBottom="60px">
                <Container>
                  <Flex marginBottom="36px" justifyContent="space-between" alignItems="center">
                    <SectionHeading>{t('common:productBundles')}</SectionHeading>
                  </Flex>
                  <Box display={{ base: 'block', md: 'none' }}>
                    <SearchForProducts redirectTo="bundles" />
                  </Box>
                </Container>
              </Box>
            </>
          )}
        </>
      ) : (
        <>
          <Container>
            <Box display={{ md: 'none' }} marginTop="20px">
              <Container>
                <Flex marginBottom="36px" justifyContent="space-between" alignItems="center">
                  <SectionHeading>{t('browseProducts')}</SectionHeading>
                </Flex>
                <SearchForProducts redirectTo="browse" />
              </Container>
            </Box>

            <CategoriesCards
              categories={categories}
              parentCategories={categoriesInfo?.parentCategories}
              location={searchParams?.location}
              startTransition={startTransition}
            />

            <Box marginTop={{ base: '20px', lg: '30px' }}>
              <CategoriesBreadcrumbs items={breadcrumbs ?? []} startTransition={startTransition} />

              <Flex
                direction="row"
                width="100%"
                justifyContent="space-between"
                alignItems="center"
                marginTop="28px"
                gap={5}
              >
                <Heading
                  as="h2"
                  lineHeight={{ base: 1.33, md: 1.2 }}
                  fontSize={{ base: 'lg', lg: '2xl' }}
                  fontWeight={400}
                >
                  {getCategoryName(categoriesInfo.category)}
                </Heading>

                <SortButton customerInfo={customerInfo} sortingInfo={sortingInfo} startTransition={startTransition} />
              </Flex>
            </Box>

            {categoriesInfo.childrenCategories.length !== 0 && (
              <Box marginTop="12px">
                <Flex flexWrap="wrap" paddingBottom="12px" gap="12px">
                  {categoriesInfo.childrenCategories.map((category) => (
                    <Box key={category.id} minWidth="98px" outline="none">
                      <TransitionLink href={getCategoryURL(category, categories)} startTransition={startTransition}>
                        <CategoryCard
                          category={{
                            id: category.id,
                            name: getCategoryName(category),
                            url: category.imageUrl,
                          }}
                        />
                      </TransitionLink>
                    </Box>
                  ))}
                </Flex>
              </Box>
            )}
          </Container>
        </>
      )}

      <Box minHeight="57vh">{isPending ? <Loader /> : <>{children}</>}</Box>

      {metadata?.last !== undefined && metadata?.last > 1 && (
        <Box marginBottom="32px">
          <Pagination
            queryParams={query}
            pageCount={metadata.last}
            currentPage={metadata.page}
            startTransition={startTransition}
          />
        </Box>
      )}
    </>
  )
}

export default BrowseNavigationContext
